import { queryOptions, useQuery } from '@tanstack/react-query'

import { equityPriceFetcher } from '@/libs/fetcher/presets'

/**
 * Batch latest-prices API for the stocks table, opened publicly on staging
 * (verified 200 on 2026-09-04):
 *   GET {equityPrice}/public/api/v1/equity/latest-prices
 *     ?asset_ids=S_NVDA.O,S_TSLA.O   (comma-separated, one page = one call)
 *     &timeframe=1D
 *     &native_currency=USD
 * Prod is served by equity-price-api.mona.co (see backendHosts in env.ts).
 */
export interface EquityLatestPrice {
  asset_id: string
  mid_price?: string | null
  regular_hours_price?: string | null
  price_percent_change?: string | null
  price_change?: string | null
  after_hours_price?: string | null
  after_hours_price_percent_change?: string | null
  pre_market_price?: {
    price?: string | null
    price_change?: string | null
    percent_change?: string | null
  } | null
  overnight_price?: {
    price?: string | null
    price_change?: string | null
    percent_change?: string | null
  } | null
  timestamp?: string
}

interface EquityLatestPricesResponse {
  native_currency?: string
  timeframe?: string
  latest?: EquityLatestPrice[]
}

export async function fetchEquityLatestPrices(assetIds: string[]): Promise<EquityLatestPrice[]> {
  if (assetIds.length === 0) return []
  const response = await equityPriceFetcher<EquityLatestPricesResponse>('/equity/latest-prices', {
    method: 'GET',
    cache: 'no-store',
    queries: {
      asset_ids: assetIds.join(','),
      timeframe: '1D',
      native_currency: 'USD',
    },
  })
  return response?.latest ?? []
}

/** 价格不带后台轮询（决策 2026-09-05：无意义地空刷），只靠 staleTime
 *  控制「多快重取」的下限；手动翻页/回到 tab 时才触发 refetch。 */
export const equityLatestPricesQueryOptions = (assetIds: string[]) =>
  queryOptions({
    queryKey: ['equityLatestPrices', assetIds],
    queryFn: () => fetchEquityLatestPrices(assetIds),
    enabled: assetIds.length > 0,
    staleTime: 30_000,
  })

export const useEquityLatestPrices = (assetIds: string[]) =>
  useQuery(equityLatestPricesQueryOptions(assetIds))
